import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useInviteAuth } from './InviteAuthContext';
import { useAuth } from '../../context/AuthContext';
import { InviteThemeProvider, useInviteTheme } from './InviteThemeContext';
import API_BASE_URL from '../../config';
import { X, Menu, Home, LayoutDashboard, Sparkles, LogOut, MessageSquare, ChevronRight, Sun, Moon, User } from 'lucide-react';
import './InviteDashboard.css';

const InviteLayoutContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { inviteUser, inviteToken, inviteLogout, loading } = useInviteAuth();
    const { user, logout, openProfileModal } = useAuth();
    const { theme, toggleTheme } = useInviteTheme();
    const history = useHistory();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isWhatsAppConnected, setIsWhatsAppConnected] = useState<boolean>(() => inviteUser?.whatsappStatus === 'connected');

    useEffect(() => {
        if (inviteUser?.whatsappStatus) {
            setIsWhatsAppConnected(inviteUser.whatsappStatus === 'connected');
        }
    }, [inviteUser?.whatsappStatus]);

    useEffect(() => {
        const checkWaStatus = async () => {
            const token = inviteToken || localStorage.getItem('invite_accessToken');
            if (!token) return;
            try {
                const res = await fetch(`${API_BASE_URL}/api/invite/whatsapp/session/status`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setIsWhatsAppConnected(data.status === 'connected');
                }
            } catch (err) {
                // keep current state
            }
        };

        checkWaStatus();
    }, [inviteToken, location.pathname]);

    const handleLogout = () => {
        localStorage.removeItem('shadigram_invite_wizard_draft');
        if (inviteUser) {
            inviteLogout();
        }
        if (user) {
            logout();
        }
        history.push('/');
    };

    const currentUser = inviteUser || (user ? { 
        id: user.id, 
        name: user.name, 
        email: user.email, 
        isPremium: false,
        maxInvitations: 10,
        totalInvitesSent: 0,
        totalFreeInvitesSent: 0,
        remainingInvites: 10 
    } : null);

    React.useEffect(() => {
        const publicPaths = ['/invite', '/invite/new', '/invite/register', '/invite/login'];
        const isPublicPath = 
            publicPaths.includes(location.pathname) || 
            location.pathname.startsWith('/invite/summary/') || 
            location.pathname.startsWith('/invite/rsvp/');

        if (!loading && !inviteUser && !isPublicPath) {
            // Redirect anonymous visitors attempting to access dashboard to the wizard
            history.push('/invite/new');
        }
    }, [inviteUser, loading, history, location.pathname]);

    React.useEffect(() => {
        setIsSidebarOpen(false);
        window.scrollTo(0, 0);
    }, [location.pathname]);

    if (loading) {
        return (
            <div className="invite-loading-screen">
                <div className="spinner"></div>
                <p>Loading Invitation Manager...</p>
            </div>
        );
    }

    const isActive = (path: string) => location.pathname === path;

    // --- ANONYMOUS / VISITOR FULL-WIDTH LAYOUT ---
    if (!inviteUser) {
        return (
            <div className={`invite-layout anonymous-layout ${theme === 'light' ? 'invite-light-theme' : ''}`} style={{ flexDirection: 'column', minHeight: '100vh', background: theme === 'light' ? '#F8FAFC' : '#090D16' }}>
                <header className="invite-header-bar" style={{ background: theme === 'light' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(15, 23, 42, 0.85)', color: theme === 'light' ? '#0F172A' : 'white', borderBottom: theme === 'light' ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                    <div className="sidebar-brand" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h2 style={{ color: theme === 'light' ? '#0F172A' : 'white', fontSize: '1.25rem', margin: 0, fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>✉️ Shadigram</h2>
                            <span style={{ fontSize: '0.72rem', background: 'rgba(139, 92, 246, 0.15)', color: '#7C3AED', border: '1px solid rgba(139, 92, 246, 0.3)', padding: '2px 8px', borderRadius: '20px', fontWeight: 700 }}>Invite Studio</span>
                        </Link>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle-btn"
                            title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: theme === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(139, 92, 246, 0.1)',
                                border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(139, 92, 246, 0.25)',
                                color: theme === 'dark' ? '#F8FAFC' : '#7C3AED',
                                padding: '5px 12px',
                                borderRadius: '20px',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {theme === 'dark' ? <Sun size={15} color="#FBBF24" /> : <Moon size={15} color="#7C3AED" />}
                            <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                        </button>
                        <Link to="/" className="back-to-home" style={{ color: theme === 'light' ? '#7C3AED' : '#94A3B8', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Home size={15} />
                            <span>Home</span>
                        </Link>
                        {currentUser ? (
                            <button
                                type="button"
                                onClick={openProfileModal}
                                className="invite-header-profile-btn"
                                title="Click to view profile"
                            >
                                <div className="invite-header-avatar">
                                    {currentUser.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="invite-header-name">{currentUser.name.split(' ')[0]}</span>
                            </button>
                        ) : (
                            <>
                                <Link to="/invite/login" style={{ color: theme === 'light' ? '#334155' : '#CBD5E1', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 600 }}>Log In</Link>
                                <Link to="/invite/register" style={{ textDecoration: 'none', background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', color: 'white', fontSize: '0.85rem', fontWeight: 700, padding: '8px 18px', borderRadius: '10px', boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)' }}>
                                    ✨ Sign Up Free
                                </Link>
                            </>
                        )}
                    </div>
                </header>
                <main className="invite-content-area" style={{ width: '100%', maxWidth: '1100px', margin: '0 auto', padding: '2.5rem 1.5rem', overflow: 'visible', flex: 1, position: 'relative', zIndex: 1 }}>
                    {children}
                </main>
            </div>
        );
    }

    // --- AUTHENTICATED SIDEBAR LAYOUT ---
    return (
        <div className={`invite-layout ${theme === 'light' ? 'invite-light-theme' : ''}`}>
            {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>}
            
            <aside className={`invite-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                <button className="sidebar-close-btn" onClick={() => setIsSidebarOpen(false)} aria-label="Close menu">
                    <X size={20} />
                </button>
                
                <div className="sidebar-brand">
                    <h2>✉️ Shadigram</h2>
                    <span>Invite Hub</span>
                </div>
                
                <div 
                    className="user-profile-badge clickable-invite-badge"
                    onClick={openProfileModal}
                    title="Click to view full profile"
                    role="button"
                    tabIndex={0}
                >
                    <div className="user-avatar">{currentUser ? currentUser.name.charAt(0).toUpperCase() : 'U'}</div>
                    <div className="user-info">
                        <h4>{currentUser ? currentUser.name : 'Creator'}</h4>
                        <span 
                            className={`plan-badge ${currentUser?.isPremium ? 'premium' : 'free'}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                history.push('/invite/upgrade');
                            }}
                            style={{ cursor: 'pointer' }}
                            title="Click to upgrade or change package"
                        >
                            {currentUser?.isPremium ? `★ Pro (${currentUser.maxInvitations || 500} Guests)` : 'Free Account (Upgrade ⚡)'}
                        </span>
                        <span className="profile-badge-link-hint">View profile details →</span>
                    </div>
                </div>
                
                <nav className="sidebar-nav">
                    <Link to="/invite/dashboard" className={`nav-item ${isActive('/invite/dashboard') ? 'active' : ''}`}>
                        <LayoutDashboard size={18} /> Dashboard
                    </Link>
                    <Link to="/invite/new" className={`nav-item ${isActive('/invite/new') ? 'active' : ''}`}>
                        <Sparkles size={18} /> New Invitation
                    </Link>
                    <Link
                        to="/invite/whatsapp/settings"
                        className={`nav-item nav-item-whatsapp ${isWhatsAppConnected ? 'wa-connected' : 'wa-disconnected'} ${isActive('/invite/whatsapp/settings') ? 'active' : ''}`}
                        title={isWhatsAppConnected ? 'WhatsApp is Connected' : 'WhatsApp is Not Connected — Click to Link'}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <MessageSquare size={18} />
                            <span>Link WhatsApp</span>
                        </div>
                        <span className={`wa-status-badge ${isWhatsAppConnected ? 'connected' : 'disconnected'}`}>
                            <span className="wa-dot" />
                            {isWhatsAppConnected ? 'Connected' : 'Not Connected'}
                        </span>
                    </Link>
                    <button
                        type="button"
                        onClick={openProfileModal}
                        className="nav-item sidebar-profile-nav-btn"
                        title="View Profile Details"
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            border: '1px solid transparent',
                            background: 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'inherit',
                            fontSize: '0.88rem',
                            padding: '0.75rem 1rem',
                            borderRadius: '10px',
                            color: theme === 'light' ? '#475569' : '#94A3B8',
                            transition: 'all 0.25s ease'
                        }}
                    >
                        <User size={18} />
                        <span>My Profile</span>
                    </button>
                    <button
                        onClick={toggleTheme}
                        className="nav-item sidebar-theme-nav-btn"
                        title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            border: '1px solid transparent',
                            background: 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'inherit',
                            fontSize: '0.88rem',
                            padding: '0.75rem 1rem',
                            marginTop: '0.25rem',
                            borderRadius: '10px',
                            color: theme === 'light' ? '#475569' : '#94A3B8',
                            transition: 'all 0.25s ease'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            {theme === 'dark' ? <Moon size={18} color="#C084FC" /> : <Sun size={18} color="#F59E0B" />}
                            <span>Theme</span>
                        </div>
                        <span style={{
                            fontSize: '0.72rem',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            background: theme === 'dark' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)',
                            color: theme === 'dark' ? '#C084FC' : '#7C3AED',
                            fontWeight: 700,
                            border: theme === 'dark' ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(139, 92, 246, 0.25)',
                            boxShadow: theme === 'light' ? '0 2px 4px rgba(0,0,0,0.03)' : 'none'
                        }}>
                            {theme === 'dark' ? 'Dark 🌙' : 'Light ☀️'}
                        </span>
                    </button>

                    {currentUser && (
                        <div 
                            className="sidebar-quota-widget"
                            onClick={() => history.push('/invite/upgrade')}
                            role="button"
                            tabIndex={0}
                            title="Click to view plan and upgrade capacity"
                        >
                            <div className="sidebar-quota-header">
                                <span className="sidebar-quota-title">
                                    {currentUser.isPremium 
                                        ? `★ ${currentUser.premiumPlan ? currentUser.premiumPlan.toUpperCase() : 'PRO'} (${currentUser.maxInvitations || 500} Guests)` 
                                        : 'Free Plan (10 Max)'}
                                </span>
                                <span className="sidebar-quota-credits">
                                    {currentUser.remainingInvites ?? Math.max(0, currentUser.maxInvitations - (currentUser.totalInvitesSent || 0))} / {currentUser.maxInvitations || 10} Left
                                </span>
                            </div>
                            <div className="sidebar-quota-progress-track">
                                <div 
                                    className="sidebar-quota-progress-fill" 
                                    style={{ 
                                        width: `${Math.min(100, Math.max(5, (((currentUser.totalInvitesSent || 0)) / (currentUser.maxInvitations || 10)) * 100))}%` 
                                    }} 
                                />
                            </div>
                            <div className="sidebar-quota-footer">
                                <span className="sidebar-quota-sent">{currentUser.totalInvitesSent || 0} Sent</span>
                                <span className="sidebar-quota-upgrade-action">Upgrade Plan ⚡</span>
                            </div>
                        </div>
                    )}

                    <div 
                        className="sidebar-nav-divider" 
                        style={{ 
                            margin: '0.5rem 0', 
                            height: '1px', 
                            background: theme === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)' 
                        }} 
                    />

                    <Link
                        to="/"
                        className="nav-item sidebar-back-nav-item"
                        title="Back to Shadigram landing page"
                    >
                        <Home size={18} />
                        <span>Back to Shadigram</span>
                    </Link>

                    {(inviteUser || user) && (
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="nav-item sidebar-logout-nav-item"
                            title="Log Out"
                        >
                            <LogOut size={18} />
                            <span>Log Out</span>
                        </button>
                    )}
                </nav>
            </aside>
            
            <div className="invite-main-wrapper">
                <header className="invite-header-bar">
                    <button className="mobile-menu-toggle" onClick={() => setIsSidebarOpen(true)} aria-label="Open menu">
                        <Menu size={22} />
                    </button>
                    
                    <nav className="header-breadcrumbs" aria-label="Breadcrumb">
                        <Link to="/invite/dashboard" className="breadcrumb-link">
                            Invite Studio
                        </Link>
                        <ChevronRight size={13} className="breadcrumb-separator" />
                        <span className="breadcrumb-current">
                            {location.pathname.includes('/new') 
                                ? 'New Invitation' 
                                : location.pathname.includes('/campaign/') 
                                    ? 'Invitation Detail' 
                                    : location.pathname.includes('/whatsapp')
                                        ? 'WhatsApp Connection'
                                        : 'Dashboard'}
                        </span>
                    </nav>
                </header>
                <main className="invite-content-area">
                    {children}
                </main>
            </div>
        </div>
    );
};

export const InviteLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <InviteThemeProvider>
            <InviteLayoutContent>
                {children}
            </InviteLayoutContent>
        </InviteThemeProvider>
    );
};

export default InviteLayout;

